import { useEffect, useState } from 'react'

import ArrowLeftIcon from '@heroicons/react/outline/esm/ArrowLeftIcon.js'
import { clsx } from 'clsx'

import { Post, PostStatus } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { useI18n } from '../i18n/providers/I18nProvider.js'
import { SpaceImage } from '../Space/SpaceImage.js'
import { PostOptions } from './PostOptions.js'
import type { PostContext } from './types.js'

export const PostHeader = ({
  post,
  context,
  className,
  showBackButton = true,
}: {
  post: Post
  context: PostContext
  className?: string
  showBackButton?: boolean
}) => {
  const { $t } = useI18n()
  const { Link } = useRouter()

  const [hasBackLink, setHasBackLink] = useState(false)
  useEffect(() => {
    setHasBackLink(window?.history?.length > 1)
  }, [])

  const onBack = () => {
    if (hasBackLink) {
      window?.history?.back()
    }
  }

  const showMoreOptions = post?.status === PostStatus.PUBLISHED

  return (
    <div className={clsx('flex space-s-3', className)}>
      {showBackButton && (
        <Button
          as={Link}
          variant="basic"
          rounded
          size="lg"
          leadingIcon={<ArrowLeftIcon className="rtl:-scale-x-100" />}
          href={hasBackLink ? undefined : post?.space?.relativeUrl}
          onClick={onBack}
          aria-label={$t({
            defaultMessage: 'Back',
            id: 'Generics.Back',
          })}
        />
      )}
      <div className="flex-1 min-w-0">
        <Button
          as={Link}
          variant="basic"
          rounded
          size="lg"
          className={clsx(
            !post?.space && 'animate-pulse',
            'max-w-full truncate inline-flex',
          )}
          href={post?.space?.relativeUrl}
        >
          <span className="flex space-s-3 items-center min-w-0">
            <span className="shrink-0">
              <SpaceImage space={post?.space} size="xs" as="span" />
            </span>
            {post?.space ? (
              <span className="truncate">{post?.space?.name}</span>
            ) : (
              <span className="h-5 w-32 bg-surface-pressed rounded-full"></span>
            )}
          </span>
        </Button>
      </div>
      {showMoreOptions && (
        <PostOptions post={post} context={context} size="lg" />
      )}
    </div>
  )
}
